import { useState, useEffect } from "react";
import { Check } from "lucide-react";

// Logo de l'application Annuaire Interne
const LOGO_URL = "https://customer-assets.emergentagent.com/job_47d6ac28-fd53-40fc-b71a-18fc6ec488aa/artifacts/50wfrc3g_AVATAR.png";

const SSO_STEPS = [
  { text: "Connexion sécurisée...", progress: 20 },
  { text: "Validation du token...", progress: 40 },
  { text: "Authentification...", progress: 60 },
  { text: "Chargement de votre profil...", progress: 80 },
  { text: "Bienvenue !", progress: 100 },
];

export default function SSOTransition({ onComplete, userName }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    const stepDuration = 600; // 600ms par étape
    
    const timer = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev < SSO_STEPS.length - 1) {
          return prev + 1;
        } else {
          clearInterval(timer);
          setIsComplete(true);
          setTimeout(() => {
            if (onComplete) onComplete();
          }, 800);
          return prev;
        }
      });
    }, stepDuration);

    return () => clearInterval(timer);
  }, [onComplete]);

  const currentProgress = SSO_STEPS[currentStep]?.progress || 0;
  const currentText = isComplete && userName 
    ? `Bienvenue ${userName} !` 
    : SSO_STEPS[currentStep]?.text || "";

  return (
    <div className="sso-transition-container">
      {/* Cercles lumineux animés en arrière-plan */}
      <div className="sso-bg-circle sso-bg-circle-1"></div>
      <div className="sso-bg-circle sso-bg-circle-2"></div>
      <div className="sso-bg-circle sso-bg-circle-3"></div>
      
      {/* Logo avec halo pulsant */}
      <div className="sso-logo-wrapper">
        <div className="sso-logo-halo"></div>
        <div className="sso-logo-halo sso-logo-halo-2"></div>
        <img src={LOGO_URL} alt="Logo" className="sso-logo" />
      </div>

      {/* Carte principale */}
      <div className="sso-card">
        <h1 className="sso-title">Annuaire Interne</h1>
        <p className="sso-subtitle">Connexion via Centrale Prysma</p>

        {/* Barre de progression */}
        <div className="sso-progress-container">
          <div 
            className="sso-progress-bar"
            style={{ width: `${currentProgress}%` }}
          ></div>
        </div>

        {/* Statut avec icône */}
        <div className="sso-status">
          {isComplete ? (
            <div className="sso-check-wrapper">
              <Check className="sso-check-icon" />
            </div>
          ) : (
            <div className="sso-spinner"></div>
          )}
          <span className={`sso-status-text ${isComplete ? 'sso-success' : ''}`}>
            {currentText}
          </span>
        </div>

        {/* Indicateurs d'étapes */}
        <div className="sso-steps">
          {SSO_STEPS.map((_, index) => (
            <div
              key={index}
              className={`sso-step-dot ${index <= currentStep ? 'sso-step-active' : ''}`}
            ></div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <p className="sso-footer">Authentification sécurisée SSO</p>

      <style>{`
        .sso-transition-container {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0a0f1a 0%, #111827 50%, #0a0f1a 100%);
          position: relative;
          overflow: hidden;
          padding: 20px;
        }

        /* Cercles lumineux animés */
        .sso-bg-circle {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.3;
          animation: float 8s ease-in-out infinite;
        }

        .sso-bg-circle-1 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, #06b6d4 0%, transparent 70%);
          top: -100px;
          left: -100px;
          animation-delay: 0s;
        }

        .sso-bg-circle-2 {
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, #3b82f6 0%, transparent 70%);
          bottom: -50px;
          right: -50px;
          animation-delay: -3s;
        }

        .sso-bg-circle-3 {
          width: 200px;
          height: 200px;
          background: radial-gradient(circle, #8b5cf6 0%, transparent 70%);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation-delay: -5s;
        }

        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -30px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }

        /* Logo avec effet halo */
        .sso-logo-wrapper {
          position: relative;
          margin-bottom: 40px;
          z-index: 10;
        }

        .sso-logo {
          width: 100px;
          height: 100px;
          border-radius: 24px;
          position: relative;
          z-index: 2;
          animation: logoFloat 3s ease-in-out infinite;
        }

        @keyframes logoFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }

        .sso-logo-halo {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 140px;
          height: 140px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(6, 182, 212, 0.4) 0%, transparent 70%);
          transform: translate(-50%, -50%);
          animation: pulse 2s ease-in-out infinite;
          z-index: 1;
        }

        .sso-logo-halo-2 {
          width: 180px;
          height: 180px;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, transparent 70%);
          animation-delay: -1s;
        }

        @keyframes pulse {
          0%, 100% { 
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.6;
          }
          50% { 
            transform: translate(-50%, -50%) scale(1.2);
            opacity: 0.3;
          }
        }

        /* Carte */
        .sso-card {
          background: rgba(17, 24, 39, 0.8);
          border: 1px solid rgba(55, 65, 81, 0.5);
          border-radius: 20px;
          padding: 32px 40px;
          width: 100%;
          max-width: 400px;
          backdrop-filter: blur(20px);
          z-index: 10;
          box-shadow: 
            0 25px 50px -12px rgba(0, 0, 0, 0.5),
            inset 0 1px 0 rgba(255, 255, 255, 0.05);
        }

        .sso-title {
          font-size: 1.75rem;
          font-weight: 700;
          color: #f9fafb;
          text-align: center;
          margin: 0 0 8px 0;
        }

        .sso-subtitle {
          font-size: 0.95rem;
          color: #9ca3af;
          text-align: center;
          margin: 0 0 28px 0;
        }

        /* Barre de progression */
        .sso-progress-container {
          height: 6px;
          background: rgba(55, 65, 81, 0.5);
          border-radius: 3px;
          overflow: hidden;
          margin-bottom: 24px;
        }

        .sso-progress-bar {
          height: 100%;
          background: linear-gradient(90deg, #06b6d4 0%, #3b82f6 50%, #8b5cf6 100%);
          border-radius: 3px;
          transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .sso-progress-bar::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.3) 50%,
            transparent 100%
          );
          animation: shimmer 1.5s infinite;
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        /* Statut */
        .sso-status {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 24px;
        }

        .sso-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(6, 182, 212, 0.2);
          border-top-color: #06b6d4;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .sso-check-wrapper {
          width: 24px;
          height: 24px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: checkPop 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }

        @keyframes checkPop {
          0% { transform: scale(0); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }

        .sso-check-icon {
          width: 14px;
          height: 14px;
          color: white;
          stroke-width: 3;
        }

        .sso-status-text {
          font-size: 0.95rem;
          color: #d1d5db;
          transition: color 0.3s ease;
        }

        .sso-status-text.sso-success {
          color: #10b981;
          font-weight: 600;
        }

        /* Indicateurs d'étapes */
        .sso-steps {
          display: flex;
          justify-content: center;
          gap: 10px;
        }

        .sso-step-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(55, 65, 81, 0.8);
          transition: all 0.3s ease;
        }

        .sso-step-dot.sso-step-active {
          background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%);
          box-shadow: 0 0 10px rgba(6, 182, 212, 0.5);
          transform: scale(1.1);
        }

        /* Footer */
        .sso-footer {
          margin-top: 32px;
          font-size: 0.85rem;
          color: #6b7280;
          z-index: 10;
        }

        /* Responsive */
        @media (max-width: 480px) {
          .sso-card {
            padding: 24px 20px;
          }
          
          .sso-title {
            font-size: 1.5rem;
          }
          
          .sso-logo {
            width: 80px;
            height: 80px;
          }
        }
      `}</style>
    </div>
  );
}
